import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { billingApi, type BillingProfile } from '@/api/billing';
import { BillingProfileContext, type BillingProfileState } from './billingProfileContext';

/** Reads the billing profile once for the page when `enabled`; see `billingProfileContext.ts`. */
export function BillingProfileProvider({
  enabled,
  children,
}: {
  enabled: boolean;
  children: ReactNode;
}) {
  const [status, setStatus] = useState<BillingProfileState['status']>(enabled ? 'loading' : 'idle');
  const [profile, setProfile] = useState<BillingProfile | null>(null);
  const [error, setError] = useState<unknown>(undefined);
  const started = useRef(false);
  const seq = useRef(0);

  const reload = useCallback(async () => {
    const mine = ++seq.current;
    setStatus('loading');
    try {
      const next = await billingApi.profile();
      if (seq.current !== mine) return;
      setProfile(next);
      setStatus('ready');
    } catch (err) {
      if (seq.current !== mine) return;
      setError(err);
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    if (!enabled || started.current) return;
    started.current = true;
    void reload();
  }, [enabled, reload]);

  const value = useMemo<BillingProfileState>(
    () =>
      enabled
        ? { status, profile, error, reload }
        : { status: 'idle', profile: null, error: undefined, reload },
    [enabled, status, profile, error, reload],
  );
  return <BillingProfileContext.Provider value={value}>{children}</BillingProfileContext.Provider>;
}
